/**
 * Permission Constants
 *
 * These permission strings must match EXACTLY with the backend permission format.
 * Backend format: "Module.Resource.Action" (e.g., "Fleet.Vehicles.Read")
 *
 * Usage:
 * - In route guards: <RequirePermission permission={Permissions.Fleet.Vehicles.Read}>
 * - In hooks: const canCreate = usePermission(Permissions.Fleet.Vehicles.Create);
 * - In components: if (can(Permissions.Fleet.Vehicles.Update)) { ... }
 */


export const Permissions = {
  Fleet: {
    Vehicles: {
      Read: 'Fleet.Vehicles.Read',
      Create: 'Fleet.Vehicles.Create',
      Update: 'Fleet.Vehicles.Update',
      Assign: 'Fleet.Vehicles.Assign',
      Delete: 'Fleet.Vehicles.Delete',
    },
    Requests: {
      Read: 'Fleet.Requests.Read',
      Create: 'Fleet.Requests.Create',
      Approve: 'Fleet.Requests.Approve',
      Reject: 'Fleet.Requests.Reject',
    },
    TravelOrders: {
      Read: 'Fleet.TravelOrders.Read',
      Create: 'Fleet.TravelOrders.Create',
      Manage: 'Fleet.TravelOrders.Manage',
      Close: 'Fleet.TravelOrders.Close',
      EditNote: 'Fleet.TravelOrders.Edit.Note',
    },
    TravelOrderPeriods: {
      Read: 'Fleet.TravelOrderPeriods.Read',
      Manage: 'Fleet.TravelOrderPeriods.Manage',
    },
    Providers: {
      Read: 'Fleet.Providers.Read',
      Manage: 'Fleet.Providers.Manage',
    },
    Contracts: {
      Read: 'Fleet.Contracts.Read',
      Manage: 'Fleet.Contracts.Manage',
    },
    Operations: {
      Read: 'Fleet.Operations.Read',
      Manage: 'Fleet.Operations.Manage',
      RegistrationManage: 'Fleet.Operations.Registration.Manage',
      MaintenanceManage: 'Fleet.Operations.Maintenance.Manage',
      FuelManage: 'Fleet.Operations.Fuel.Manage',
    },
    FleetUsers: {
      Read: 'Fleet.FleetUsers.Read',
      Create: 'Fleet.FleetUsers.Create',
      Update: 'Fleet.FleetUsers.Update',
    },
    Tires: {
      Read: 'Fleet.Tires.Read',
      Create: 'Fleet.Tires.Create',
      Update: 'Fleet.Tires.Update',
      Delete: 'Fleet.Tires.Delete',
    },
  },
  Users: {
    Users: {
      Read: 'Users.Read',
      Create: 'Users.Create',
      Update: 'Users.Update',
      Archive: 'Users.Archive',
      Deactivate: 'Users.Deactivate',
    },
    Roles: {
      Read: 'Users.Roles.Read',
      Create: 'Users.Roles.Create',
      Update: 'Users.Roles.Update',
      Assign: 'Users.Roles.Assign',
    },
    Permissions: {
      Assign: 'Users.Permissions.Assign',
    },
  },
} as const;

// Type helper for permission strings - provides autocomplete and type safety
export type PermissionString =
  | (typeof Permissions.Fleet.Vehicles)[keyof typeof Permissions.Fleet.Vehicles]
  | (typeof Permissions.Fleet.Requests)[keyof typeof Permissions.Fleet.Requests]
  | (typeof Permissions.Fleet.TravelOrders)[keyof typeof Permissions.Fleet.TravelOrders]
  | (typeof Permissions.Fleet.TravelOrderPeriods)[keyof typeof Permissions.Fleet.TravelOrderPeriods]
  | (typeof Permissions.Fleet.Providers)[keyof typeof Permissions.Fleet.Providers]
  | (typeof Permissions.Fleet.Contracts)[keyof typeof Permissions.Fleet.Contracts]
  | (typeof Permissions.Fleet.Operations)[keyof typeof Permissions.Fleet.Operations]
  | (typeof Permissions.Fleet.FleetUsers)[keyof typeof Permissions.Fleet.FleetUsers]
  | (typeof Permissions.Fleet.Tires)[keyof typeof Permissions.Fleet.Tires]
  | (typeof Permissions.Users.Users)[keyof typeof Permissions.Users.Users]
  | (typeof Permissions.Users.Roles)[keyof typeof Permissions.Users.Roles]
  | (typeof Permissions.Users.Permissions)[keyof typeof Permissions.Users.Permissions];

/**
 * Helper function to check if a string is a valid permission
 */
export const isValidPermission = (permission: string): permission is PermissionString => {
  const allPermissions = Object.values(Permissions).flatMap((module) =>
    Object.values(module).flatMap((resource) => Object.values(resource))
  );
  return allPermissions.includes(permission);
};
